/**
 * A TRANCA DO MÓDULO DE CONVERSAS.
 *
 * O módulo continua inteiro no código — rotas, componentes, store, API. O que este arquivo faz é
 * decidir o que fica alcançável.
 *
 * MOTIVO ORIGINAL (03/09/2026): as conversas são compartilhadas entre a rede inteira. Um consultor
 * com o número pessoal conectado teve a conversa dele visível para quem não devia ver. Enquanto não
 * existir segmentação por dono da caixa, o módulo saiu do ar para todos.
 *
 * REABERTURA PARCIAL (09/09/2026): trancar as ROTAS junto com o menu quebrou o que dependia de
 * CHEGAR numa conversa específica — o "ver conversa" do lead e o destravamento de lead que está em
 * atendimento com outro consultor. Os dois eram fluxo de trabalho, não navegação, e não havia
 * destino equivalente para onde mandá-los. Por isso a tranca virou DUAS.
 *
 * O DESTINO EXISTE (03/10/2026, pedido do dono do produto): o Pipeline abre o drawer do lead a
 * partir do display_id da conversa — `?conversa=` em Kanban2Index, resolvido pelo endpoint v2
 * `kanban_items/by_conversation`. Com destino, as duas trancas fecham:
 *
 *   · quem tem um ponteiro para uma conversa (botão, link, URL, e-mail antigo, push) é levado ao
 *     LEAD daquela conversa, com as mensagens à vista;
 *   · o módulo continua SEM VITRINE — não está no menu, não tem atalho, não aparece na busca.
 *
 * ⚠️ O vazamento de 03/09 deixa de ser alcançável pela URL: ela não serve mais a tela de
 * Conversas. O drawer do lead mostra só o que a política do Kanban deixa aquela pessoa ver
 * (KanbanItemPolicy::Scope decide qual oportunidade o `?conversa=` abre). A segmentação por dono
 * da caixa continua não existindo — o que mudou é que não há mais tela que a dispense.
 *
 * O que NÃO é bloqueado, de propósito: a API de conversas continua servindo o servidor, porque o
 * Kanban 2.0 depende dela (`conversations/:id/negotiation` e `conversations/:id/messages` em
 * api/kanban.js). Fechar o endpoint derrubaria o Kanban junto.
 */

/**
 * Rotas de conversa (as 18 de conversation.routes.js). FECHADAS DE NOVO (03/10/2026).
 *
 * Em 09/09 elas foram reabertas porque trancar a ROTA junto com o menu quebrou o que dependia de
 * CHEGAR numa conversa específica: não havia para onde mandar quem clicava. Agora há — o Pipeline
 * abre o drawer do lead a partir do display_id da conversa (`?conversa=`, Kanban2Index + o
 * endpoint v2 `kanban_items/by_conversation`), e é esse o destino.
 *
 * As rotas continuam REGISTRADAS, com os mesmos caminhos e nomes: a que carrega
 * `:conversation_id` redireciona para o Pipeline daquela conversa; as de LISTA, que não têm
 * conversa nenhuma para mostrar, seguem indo para o Ranking como em 03/09.
 *
 * O que isto alcança e nenhuma troca de botão alcançaria: e-mail antigo de notificação, push,
 * link copiado no grupo, favorito, universal link do app iOS e o SSO com conversa alvo. Todos
 * entram pela URL, e todos passam a desaguar no lead.
 */
export const ROTAS_DE_CONVERSA_BLOQUEADAS = true;

/**
 * Vitrine do módulo: item da sidebar, "Nova conversa", atalho Alt+C, abas de Conversas/Mensagens
 * na busca global e a lista do painel de contato. CONTINUAM FECHADAS — são descoberta, não
 * trabalho, e a busca global é justamente a superfície que exibia conversa de quem não devia.
 */
export const CONVERSAS_OCULTAS_NA_NAVEGACAO = true;
/**
 * AS ROTAS DO MÓDULO, PELO NOME. Fonte única das duas camadas da tranca: o `redirect` de cada
 * rota (conversation.routes.js e inbox/routes.js) e o guard global do roteador (routes/index.js).
 *
 * São as 18 de conversation.routes.js mais as 2 do módulo Inbox (`inbox-view`), que renderiza a
 * MESMA tela de chat por outro caminho — era a segunda porta, fechada em 05/10/2026.
 */
export const ROTAS_DO_MODULO_DE_CONVERSAS = Object.freeze([
  'home',
  'inbox_conversation',
  'inbox_dashboard',
  'conversation_through_inbox',
  'label_conversations',
  'conversations_through_label',
  'team_conversations',
  'conversations_through_team',
  'folder_conversations',
  'conversations_through_folders',
  'conversation_mentions',
  'conversation_through_mentions',
  'conversation_unattended',
  'conversation_through_unattended',
  'conversation_unread',
  'conversation_through_unread',
  'conversation_participating',
  'conversation_through_participating',
  'inbox_view',
  'inbox_view_conversation',
]);

/**
 * OS MESMOS CAMINHOS, pelo formato da URL. Redundante de propósito: o nome só existe se a rota
 * estiver registrada com ele. Se uma rota do módulo for renomeada, duplicada em outro arquivo ou
 * registrada sem nome, o caminho ainda a entrega.
 *
 * Cada padrão é ancorado no formato do módulo para não pegar vizinho: `/dashboard` termina a URL
 * (dashboard-apps tem outro caminho), `/inbox/` é a lista por caixa (as telas de configuração
 * moram em `/settings/inboxes/`), e `label`/`team`/`custom_view` só contam quando seguidos de
 * `/conversations` ou no formato exato da lista do módulo.
 */
export const CAMINHOS_DO_MODULO_DE_CONVERSAS = Object.freeze([
  /\/accounts\/[^/]+\/dashboard\/?$/,
  /\/accounts\/[^/]+\/conversations(\/|$)/,
  /\/accounts\/[^/]+\/inbox\/[^/]+(\/|$)/,
  /\/accounts\/[^/]+\/inbox-view(\/|$)/,
  /\/accounts\/[^/]+\/(label|team|custom_view)\/[^/]+\/?$/,
  /\/accounts\/[^/]+\/(label|team|custom_view)\/[^/]+\/conversations(\/|$)/,
  /\/accounts\/[^/]+\/(mentions|unattended|unread|participating)\/conversations(\/|$)/,
]);

/**
 * Esta rota é do módulo de conversas? Nome OU caminho — basta um.
 */
export const ehRotaDoModuloDeConversas = (to = {}) =>
  ROTAS_DO_MODULO_DE_CONVERSAS.includes(to.name) ||
  CAMINHOS_DO_MODULO_DE_CONVERSAS.some(padrao => padrao.test(to.path || ''));

/**
 * O display_id da conversa que a rota carrega, em qualquer das formas que o módulo usa:
 *
 *   · `:conversation_id` — conversa direta, caixa, etiqueta, pasta;
 *   · `:conversationId`  — equipe, menções, não atendidas, não lidas, participando. A grafia em
 *     camelCase é do Chatwoot, e ler só a outra mandava CINCO rotas para o lugar errado (bug de
 *     03/10, pego em 05/10);
 *   · `:type/:id` com `type === 'conversation'` — o módulo Inbox (`inbox-view`), onde o mesmo
 *     caminho serve notificação e conversa.
 *
 * Sem conversa, devolve null: é tela de LISTA, e lista não tem lead para abrir.
 */
export const idDaConversaNaRota = ({ params = {} } = {}) => {
  const direto = params.conversation_id ?? params.conversationId;
  if (direto) return direto;
  if (String(params.type) === 'conversation' && params.id) return params.id;

  return null;
};

/**
 * PARA ONDE VAI QUEM CHEGA. Sempre o Pipeline: com `?conversa=` quando a rota traz uma conversa
 * (o drawer do lead abre nela), e sem nada quando é tela de lista.
 *
 * Caminho montado à mão, e não `{ name: 'kanban_2_0_board' }`: o guard global não pode depender de
 * a rota de destino estar registrada com aquele nome. Um erro aqui não deve virar erro de
 * navegação em cima de outro.
 */
export const enderecoForaDoModuloDeConversas = (
  to = {},
  contaDeReserva = null
) => {
  const accountId = to?.params?.accountId || contaDeReserva;
  const conversa = idDaConversaNaRota(to);
  const base = `/app/accounts/${accountId}/kanban2`;

  return conversa ? `${base}?conversa=${conversa}` : base;
};

/**
 * REMOVIDAS em 03/10/2026: `CONVERSAS_BLOQUEADAS` e `AVISO_CONVERSAS_BLOQUEADAS`.
 *
 * Eram a tranca ÚNICA de 03/09 e a frase de recusa. Dez telas as usavam para decidir SE
 * NAVEGAVAM — FloatingCallWidget, captain/responses (Index e Pending), KanbanCard do 2.0, quatro
 * componentes do kanban legado, ListTab e SLAReportItem — e todas as dez paravam no aviso, porque
 * não havia destino. Agora há: cada uma manda para o lead no Pipeline
 * (`URLHelper#pipelineLeadUrl`), e o aviso não tem mais o que dizer.
 *
 * Um botão NOVO que levava à conversa não precisa de constante nenhuma: use `pipelineLeadUrl`.
 * Quem não tiver o display_id da conversa em mão não tem o que abrir — e isso não é caso de
 * tranca, é falta de dado.
 */
